import { useContext, Component } from 'react';

import Modal from 'react-bootstrap/Modal';
import { Container } from 'reactstrap';
import { Button } from 'reactstrap';
import { AuthenticationContext } from '../../../msal/AuthenticationContext';
import { NotificationContext } from '../../../context/NotificationContext';
import { TenantContext } from '../../../context/TenantContext';
import { ContactGroupEntries } from './ContactGroupEntries';
import { ModalContactGroupAddOrUpdate } from './ModalContactGroupAddOrUpdate';
import * as Utils from '../../../utils/helper';
import * as Misc from '../../../utils/misc';
import * as apiCalls from '../../../logic/apiCalls';
import { ButtonWithDisabledTooltip } from '../../../components/ButtonWithDisabledTooltip';
import { Alert } from 'react-bootstrap';

// The maximum allowed number of contactGroups per tenant.
const maximumNumberOfContactGroups = 10;

class ContactGroupSettingsInternal extends Component {
    refreshContactGroupsTimer = 0;
    refreshContactGroupsTimeoutTimer = 0;

    state = {
        isLoading: true,
        contactGroups: [],
        contacts: [],
        contactGroupToDelete: {},
        contactGroupToUpdate: {},
        isConfirmDeleteModalOpen: false,
        isContactGroupModalOpen: false,
        isContactGroupModalUpdate: false,
    }

    async componentDidMount() {
        this.getContactGroups();
        this.getContacts();
    }

    async componentWillUnmount() {
    }

    showConfirmDeleteModal = () => {
        this.setState({
            isConfirmDeleteModalOpen: true,
        });
    };

    hideConfirmDeleteModal = () => {
        this.setState({
            isConfirmDeleteModalOpen: false,
        });
    };

    showAddContactGroupModal = () => {
        this.setState({
            isContactGroupModalUpdate: false,
            isContactGroupModalOpen: true,
        });
    };

    showUpdateContactGroupModal = () => {
        this.setState({
            isContactGroupModalUpdate: true,
            isContactGroupModalOpen: true,
        });
    };

    hideContactGroupModal = () => {
        this.setState({
            isContactGroupModalOpen: false,
        });
    }
    
    getContactGroups = async () => {
        try {
            console.log("[getContactGroups] Invoking the back-end API to get all contact groups of this tenant.");

            const contactGroups = await apiCalls.getContactGroups(this.props.authenticationContext);

            this.setState({
                contactGroups: contactGroups,
            });               
        }
        catch (err) {
            console.log(`[getContactGroups] Error retrieving the contact groups: ${err}`);
            this.props.notificationContext.setCommunicationFailureNotification();               
        }
        finally {
            this.setState({
                isLoading: false,
            });
        }
    }

    getContacts = async () => {
        try {
            console.log("[getContacts] Invoking the back-end API to get all contacts of this tenant.");

            const contacts = await apiCalls.getContacts(this.props.authenticationContext);

            this.setState({
                contacts: contacts,
            });               
        }
        catch (err) {
            console.log(`[getContacts] Error retrieving the contacts: ${err}`);
            this.props.notificationContext.setCommunicationFailureNotification();               
        }
    }

    handleContactGroupDelete = (contactGroup) => {
        this.setState({
            contactGroupToDelete: contactGroup,
        });
        this.showConfirmDeleteModal();
    }

    handleContactGroupEdit = (contactGroup) => {
        this.setState({
            contactGroupToUpdate: contactGroup,
        });
        this.showUpdateContactGroupModal();
    }

    executeDeleteContactGroup = async () => {
        try {
            console.log(`[deleteContactGroup] Invoking the back-end API to delete the contact group with Id ${this.state.contactGroupToDelete.contactGroupId}.`);

            await apiCalls.deleteContactGroup(this.props.authenticationContext, this.state.contactGroupToDelete.contactGroupId);

            this.props.notificationContext.setNotification("Deleting contact group.", `'${this.state.contactGroupToDelete.name}' successfully deleted.`, 'success');

            this.getContactGroups();
        }
        catch (err) {
            this.props.notificationContext.setNotification("Deleting contact group.", `Error deleting '${this.state.contactGroupToDelete.name}'. ${err.toDetailedMessage()}`, 'danger');                  
        }

        this.hideConfirmDeleteModal();
    }

    executeAddOrUpdate = async (doneInfo) => {
        const contactGroupAction = doneInfo.isUpdate ? 'updating' : 'adding';
        const contactGroupActionDone = doneInfo.isUpdate ? 'updated' : 'added';

        try {
            console.log(`[executeAddOrUpdate] Invoking the back-end API ${contactGroupAction} the contactGroup.`);

            if (doneInfo.isUpdate)
            {
                await apiCalls.updateContactGroup(this.props.authenticationContext, doneInfo.contactGroupId, doneInfo.name, doneInfo.description);

                for (const contact of doneInfo.contactsAdded) {
                    await apiCalls.addContactGroupEntry(this.props.authenticationContext, doneInfo.contactGroupId, contact.contactId);
                };

                for (const contact of doneInfo.contactsRemoved) {
                    await apiCalls.deleteContactGroupEntry(this.props.authenticationContext, doneInfo.contactGroupId, contact.contactId);
                };
            }
            else
            {
                await apiCalls.addContactGroup(this.props.authenticationContext, doneInfo.name, doneInfo.description);

                const contactGroups = await apiCalls.getContactGroups(this.props.authenticationContext);
                const newContactGroup = contactGroups.find(cg => cg.name === doneInfo.name);

                if (newContactGroup)
                {
                    for (const contact of doneInfo.contactsAdded) {
                        await apiCalls.addContactGroupEntry(this.props.authenticationContext, newContactGroup.contactGroupId, contact.contactId);
                    };
                }
            }

            this.props.notificationContext.setNotification(`${Misc.capitalizeFirstLetter(contactGroupAction)} contactGroup.`, `'${doneInfo.name}' successfully ${contactGroupActionDone}.`, 'success');
            this.getContactGroups();
        }
        catch (err) {
            this.props.notificationContext.setNotification(`${Misc.capitalizeFirstLetter(contactGroupAction)} contactGroup.`, `Error ${contactGroupAction} '${doneInfo.name}'. ${err.toDetailedMessage()}`, 'danger');
        }
        finally {
            this.hideContactGroupModal();
        }
    }

    shouldAddContactGroupButtonBeEnabled = () => {
        if (this.state.contactGroups.length >= maximumNumberOfContactGroups) {
            return false;
        }

        return true;
    }

    // ------------------------------
    // Rendering starts here...
    // ------------------------------
    render () {
        const headerRendered = <h1>Contact Group settings</h1> 

        if (this.state.isLoading) {
            return (
                <Container fluid>
                    {headerRendered}
                    {this.props.tenantContext.showWarningWhenDisabled()}
                    <p><em>Loading...</em></p>
                </Container>
            );
        }

        const warningText = Utils.isTrue(this.state.contactGroupToDelete.enabled) ? `The contact group '${this.state.contactGroupToDelete.description}' will no longer be notified of any events.` : "";
        
        const confirmDeleteModalRendered = <>
            <Modal show={this.state.isConfirmDeleteModalOpen} onHide={this.hideConfirmDeleteModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete contact group '{this.state.contactGroupToDelete.description}'?<br/>    
                    {warningText}           
                </Modal.Body>
                <Modal.Footer>
                    <Button color="secondary" onClick={this.hideConfirmDeleteModal}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={this.executeDeleteContactGroup}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </>

        const contactGroups = this.state.contactGroups;

        const contactGroupsRendered = contactGroups.length > 0 ? 
            <ContactGroupEntries tenant={this.props.tenantContext} contactGroups={contactGroups} handleDelete={this.handleContactGroupDelete} handleEdit={this.handleContactGroupEdit}/> : 
            <Alert className="mt-3" variant='warning' transition={false}>
                No contact groups configured. Please add a contact group which will be notified by the Zorgmessenger service.
            </Alert>;

        return (
            <Container fluid>
                {confirmDeleteModalRendered}
                <ModalContactGroupAddOrUpdate isOpen={this.state.isContactGroupModalOpen} isUpdate={this.state.isContactGroupModalUpdate} tenant={this.props.tenantContext} contactGroupToUpdate={this.state.contactGroupToUpdate} contacts={this.state.contacts} onHide={this.hideContactGroupModal} onAddOrUpdate={this.executeAddOrUpdate}/>
                {headerRendered}
                {this.props.tenantContext.showWarningWhenDisabled()}
                <hr/>
                <ButtonWithDisabledTooltip color="primary" disabled={!this.shouldAddContactGroupButtonBeEnabled()} disabledTooltip="Maximum number of contact groups reached" onClick={this.showAddContactGroupModal}>Add contactGroup</ButtonWithDisabledTooltip>
                {contactGroupsRendered}
                <br/>
            </Container>
        );

    }
}

// Wrap the component so that multiple contexts are available via the props.
export const ContactGroupSettings = (props) => {
    const authenticationContext = useContext(AuthenticationContext);
    const notificationContext = useContext(NotificationContext);
    const tenantContext = useContext(TenantContext);
  
    return (
        <ContactGroupSettingsInternal authenticationContext={authenticationContext} notificationContext={notificationContext} tenantContext={tenantContext} {...props}/>
    )
}